import * as types from 'constants/ActionTypes';
import { CUSTOMERS_API_URL, CUSTOMERS_FILTER_URL, CUSTOMERS_URL } from 'constants/ApiConstants';

import { resolveLink } from '../services/UrlResolver';

/**
 * @param {import('modules/customers/types/actions').FetchCustomersParams} params
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').FetchCustomersAction}
 */
export const fetchCustomers = (params, meta = {}) => ({
  type: types.FETCH_CUSTOMERS_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: CUSTOMERS_FILTER_URL,
      params,
    },
  },
  meta,
});

/**
 * @param {import('modules/global/generic.types').PageContent<import('modules/customers/types/projections/paginated-customer').PaginatedCustomer>} data
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').FetchCustomersSuccessAction}
 */
export const fetchCustomersSuccess = (data, meta = {}) => ({
  type: types.FETCH_CUSTOMERS_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').FetchCustomersFailureAction}
 */
export const fetchCustomersFailure = (error, meta = {}) => ({
  type: types.FETCH_CUSTOMERS_FAILURE,
  payload: error,
  error: true,
  meta,
});

// FETCH CUSTOMERS FOR SELECT
/**
 * @param {string} links
 * @param {Object} parameters
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').FetchCustomersSelectAction}
 */
export const fetchCustomersSelect = (links, parameters, meta = {}) => {
  const { url, params } = resolveLink(links, CUSTOMERS_FILTER_URL, parameters);
  return {
    type: types.FETCH_CUSTOMERS_SELECT_REQUEST,
    payload: {
      request: {
        method: 'GET',
        url,
        params,
      },
      links,
    },
    meta,
  };
};

/**
 * @param {import('modules/global/generic.types').PageContent<import('modules/customers/types/projections/paginated-customer').PaginatedCustomer>} data
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').FetchCustomersSelectSuccessAction}
 */
export const fetchCustomersSelectSuccess = (data, meta = {}) => ({
  type: types.FETCH_CUSTOMERS_SELECT_SUCCESS,
  payload: data,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').FetchCustomersSelectFailureAction}
 */
export const fetchCustomersSelectFailure = (error, meta = {}) => ({
  type: types.FETCH_CUSTOMERS_SELECT_FAILURE,
  payload: error,
  error: true,
  meta,
});

// FETCH ONE CUSTOMER
/**
 * @param {string} customerId
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').FetchCustomerAction}
 */
export const fetchCustomer = (customerId, meta = {}) => ({
  type: types.FETCH_CUSTOMER_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${CUSTOMERS_URL}/${customerId}`,
    },
  },
  meta,
});

/**
 * @param {import('modules/customers/types/projections/detailed-customer').DetailedCustomer} customer
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').FetchCustomerSuccessAction}
 */
export const fetchCustomerSuccess = (customer, meta = {}) => ({
  type: types.FETCH_CUSTOMER_SUCCESS,
  payload: customer,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').FetchCustomerFailureAction}
 */
export const fetchCustomerFailure = (error, meta = {}) => ({
  type: types.FETCH_CUSTOMER_FAILURE,
  payload: error,
  error: true,
  meta,
});

// FETCH ONE CUSTOMER FOR SELECT
/**
 * @param {string} id - Customer Id
 * @param {Object} params
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').FetchCustomerSelectAction}
 */
export const fetchCustomerSelect = (id, params, meta = {}) => ({
  type: types.FETCH_CUSTOMER_SELECT_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${CUSTOMERS_URL}/${id}`,
      params,
    },
  },
  meta,
});

/**
 * @param {import('modules/customers/types/projections/detailed-customer').DetailedCustomer} customer
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').FetchCustomerSelectSuccessAction}
 */
export const fetchCustomerSelectSuccess = (customer, meta = {}) => ({
  type: types.FETCH_CUSTOMER_SELECT_SUCCESS,
  payload: customer,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').FetchCustomerSelectFailureAction}
 */
export const fetchCustomerSelectFailure = (error, meta = {}) => ({
  type: types.FETCH_CUSTOMER_SELECT_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').CleanCustomersSelectAction}
 */
export const cleanCustomersSelect = (meta = {}) => ({
  type: types.CLEAN_CUSTOMERS_SELECT,
  payload: undefined,
  meta
});

// CUSTOMER EXPORT
/**
 * @param {string} customerId
 * @param {string} filenames
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').ExportCustomerRequestAction}
 */
export const exportCustomerRequest = (customerId, filenames, meta = {}) => ({
  type: types.EXPORT_CUSTOMER_REQUEST,
  payload: {
    requests: {
      interventionRequest: {
        method: 'GET',
        url: `${CUSTOMERS_URL}/${customerId}/interventions`,
      },
      auditRequest: {
        method: 'GET',
        url: `${CUSTOMERS_URL}/${customerId}/audits`,
      },
    },
    customerId,
    filenames,
  },
  meta,
});

/**
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').ExportCustomerSuccessAction}
 */
export const exportCustomerSuccess = (meta = {}) => ({
  type: types.EXPORT_CUSTOMER_SUCCESS,
  payload: undefined,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').ExportCustomerFailureAction}
 */
export const exportCustomerFailure = (error, meta = {}) => ({
  type: types.EXPORT_CUSTOMER_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').DisplayCreationModalAction}
 */
export const displayCreationModal = (meta = {}) => ({
  type: types.DISPLAY_CREATION_MODAL,
  payload: undefined,
  meta,
});

/**
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').HideCreationModalAction}
 */
export const hideCreationModal = (meta = {}) => ({
  type: types.HIDE_CREATION_MODAL,
  payload: undefined,
  meta
});

/**
 * @param {import('modules/customers/types/customer-data').CustomerData} data
 * @param {{ tva?: string, dealerId?: string}} [additionalData={}]
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').CreateCustomerAction}
 */
export const createCustomer = (data, additionalData = {}, meta = {}) => ({
  type: types.CREATE_CUSTOMER_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: `${CUSTOMERS_API_URL}`,
      data,
    },
    additionalData,
  },
  meta,
});

/**
 * @param {import('modules/customers/types/projections/detailed-customer').DetailedCustomer} customer
 * @returns {import('modules/customers/types/actions').CreateCustomerSuccessAction}
 */
export const createCustomerSuccess = customer => ({
  type: types.CREATE_CUSTOMER_SUCCESS,
  payload: customer,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').CreateCustomerFailureAction}
 */
export const createCustomerFailure = (error, meta = {}) => ({
  type: types.CREATE_CUSTOMER_FAILURE,
  payload: error,
  error: true,
  meta
});

/**
 * @param {import('modules/customers/types/projections/detailed-customer').DetailedCustomer} data
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').UpdateCustomerAction}
 */
export const updateCustomer = (data, meta = {}) => ({
  type: types.UPDATE_CUSTOMER_REQUEST,
  payload: {
    request: {
      method: 'PUT',
      url: `${CUSTOMERS_API_URL}/${data.id}`,
      data,
    },
  },
  meta,
});

/**
 * @param {import('modules/customers/types/projections/detailed-customer').DetailedCustomer} customer
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').UpdateCustomerSuccessAction}
 */
export const updateCustomerSuccess = (customer, meta = {}) => ({
  type: types.UPDATE_CUSTOMER_SUCCESS,
  payload: customer,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').UpdateCustomerFailureAction}
 */
export const updateCustomerFailure = (error, meta = {}) => ({
  type: types.UPDATE_CUSTOMER_FAILURE,
  payload: error,
  error: true,
  meta,
});

// DELETE CUSTOMER
/**
 * @param {string} customerId
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').DeleteCustomerRequestAction}
 */
export const deleteCustomerRequest = (customerId, meta = {}) => ({
  type: types.DELETE_CUSTOMER_REQUEST,
  payload: {
    request: {
      method: 'DELETE',
      url: `${CUSTOMERS_API_URL}/${customerId}`,
    },
    customerId,
  },
  meta
});

/**
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').DeleteCustomerSuccessAction}
 */
export const deleteCustomerSuccess = (meta = {}) => ({
  type: types.DELETE_CUSTOMER_SUCCESS,
  payload: undefined,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import('modules/customers/types/actions').DeleteCustomerFailureAction}
 */
export const deleteCustomerFailure = (error, meta = {}) => ({
  type: types.DELETE_CUSTOMER_FAILURE,
  payload: error,
  error: true,
  meta,
});
